/**
 * Auth slice - Redux state for auth
 * Fast reads from store; HTTP goes through RTK Query authApi
 */
import { createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { logoutOneSignal } from '../../services/oneSignalPush';
import { authApi } from '../api/authApi';
import { baseApi } from '../api/baseApi';

/** Run an authApi endpoint and return an axios-like { data, headers, status }. */
async function authRequest(dispatch, endpoint, arg) {
  const result = await dispatch(
    authApi.endpoints[endpoint].initiate(arg, { forceRefetch: true })
  );
  if (result.error) {
    const err = new Error(
      result.error?.data?.message ||
        (typeof result.error?.data === 'string' ? result.error.data : null) ||
        'Request failed'
    );
    err.response = { status: result.error.status, data: result.error.data };
    err.apiMessage =
      typeof result.error?.data === 'string'
        ? result.error.data
        : result.error?.data?.message;
    throw err;
  }
  return {
    data: result.data,
    headers: result.meta?.headers ?? {},
    status: result.meta?.status,
  };
}

const getInitialState = () => {
  const token = localStorage.getItem('token');
  const userStr = localStorage.getItem('user');
  const impersonating = token && localStorage.getItem('impersonating') === 'true';
  if (token && userStr) {
    try {
      const user = JSON.parse(userStr);
      return {
        user,
        permissions: [],
        isAuthenticated: true,
        loading: true,
        permissionsReady: false,
        impersonating: !!impersonating,
      };
    } catch (e) {
      // Invalid JSON in localStorage, ignore
    }
  }
  return {
    user: null,
    permissions: [],
    isAuthenticated: false,
    loading: true,
    permissionsReady: false,
    impersonating: false,
  };
};

const initialState = getInitialState();

/** Normalize permission name for consistent matching (dba-create, dba create, dba_create) */
const normalizePermission = (name) =>
  (name || '').toLowerCase().replace(/\s+/g, '-').replace(/_/g, '-');

const extractPermissionNames = (response) => {
  const permsData = response?.data?.data?.permissions || response?.data || [];
  return (Array.isArray(permsData) ? permsData : [])
    .map((p) => (typeof p === 'string' ? p : p?.name || p?.permission_name))
    .filter(Boolean)
    .map(normalizePermission);
};

/** Extract permission names from user object or profile response */

// Commented out because its not used anymore, but kept for reference in case we need it in the future

// const extractUserPermissionNames = (userData) => {
//   const perms = userData?.permissions ?? userData?.data?.permissions ?? [];
//   const arr = Array.isArray(perms) ? perms : [];
//   return arr
//     .map((p) => (typeof p === 'string' ? p : p?.name || p?.permission_name))
//     .filter(Boolean)
//     .map(normalizePermission);
// };

/** Extract permissions from /auth/profile response - supports multiple shapes */
const extractProfilePermissions = (profileResponse) => {
  const data = profileResponse?.data ?? profileResponse;
  const user = data?.user ?? data?.data;
  const perms =
    user?.permissions ??
    data?.permissions ??
    data?.data?.permissions ??
    [];
  const arr = Array.isArray(perms) ? perms : [];
  return arr
    .map((p) => (typeof p === 'string' ? p : p?.name || p?.permission_name))
    .filter(Boolean)
    .map(normalizePermission);
};

/** Merge role permissions with user permissions (deduplicated) */
const mergePermissions = (rolePerms, userPerms) => {
  const set = new Set([...(rolePerms || []), ...(userPerms || [])]);
  return [...set];
};

export const getUserId = (user) => {
  if (user == null) return null;
  if (typeof user === 'string' || typeof user === 'number') return String(user);
  const id = user?.id ?? user?.uuid;
  return id != null ? String(id) : null;
};

export const isSameUser = (a, b) => {
  const idA = getUserId(a);
  const idB = getUserId(b);
  if (idA && idB) return idA === idB;
  if (!a || !b || typeof a !== 'object' || typeof b !== 'object') return false;
  return (a?.email || a?.username) === (b?.email || b?.username);
};

/** Parse token + user from login / impersonate / leave responses. */
export const extractAuthPayload = (axiosResponse) => {
  const layers = [
    axiosResponse?.data,
    axiosResponse?.data?.data,
    axiosResponse?.data?.data?.data,
  ].filter((layer) => layer && typeof layer === 'object');

  let token = null;
  let user = null;
  let impersonating;

  for (const layer of layers) {
    token =
      token ??
      layer.token ??
      layer.access_token ??
      layer.plainTextToken ??
      layer.authentication?.token;

    if (layer.impersonating === true || layer.impersonating === false) {
      impersonating = layer.impersonating;
    }

    const candidate = layer.user;
    if (candidate && typeof candidate === 'object' && (candidate.id || candidate.uuid || candidate.email)) {
      user = user ?? candidate;
    } else if (!user && (layer.id || layer.uuid) && layer.email) {
      user = layer;
    }
  }

  const authHeader =
    axiosResponse?.headers?.authorization ?? axiosResponse?.headers?.Authorization;
  if (!token && authHeader && /^Bearer\s+/i.test(String(authHeader))) {
    token = String(authHeader).replace(/^Bearer\s+/i, '').trim();
  }

  return { token, user, impersonating };
};

const clearImpersonationStorage = () => {
  localStorage.removeItem('impersonating');
  localStorage.removeItem('impersonation_parent_token');
};

const setImpersonationStorage = (active) => {
  if (active) localStorage.setItem('impersonating', 'true');
  else localStorage.removeItem('impersonating');
};

const loadPermissionsForProfile = async (dispatch, profileRes, profileUser) => {
  const profilePerms = extractProfilePermissions(profileRes);
  const roleId = profileUser?.role?.id || profileUser?.roles?.[0]?.id;
  let rolePerms = [];
  if (roleId) {
    try {
      const permRes = await authRequest(dispatch, 'getRoleByIdForAuth', roleId);
      rolePerms = extractPermissionNames(permRes);
    } catch (e) {
      // Role fetch may fail; use profile perms only
    }
  }
  return mergePermissions(rolePerms, profilePerms);
};

const hydrateSessionAfterTokenChange = async (dispatch, token, options = {}) => {
  const { clearImpersonation = true } = options;
  localStorage.setItem('token', token);
  if (clearImpersonation) {
    clearImpersonationStorage();
    dispatch(setImpersonating(false));
  }

  const profileRes = await authRequest(dispatch, 'getProfile');
  const profileData = profileRes?.data ?? profileRes;
  const profileUser = profileData?.user ?? profileData?.data ?? profileData;

  if (profileUser) {
    dispatch(setCredentials(profileUser));
    localStorage.setItem('user', JSON.stringify(profileUser));
  }

  const permissions = await loadPermissionsForProfile(dispatch, profileRes, profileUser);
  dispatch(setPermissions(permissions));
  dispatch(setPermissionsReady(true));

  return { profileUser, profileRes, permissions };
};

const applyImpersonationFlag = (dispatch, profileUser, responseImpersonating) => {
  if (typeof profileUser?.impersonating === 'boolean') {
    dispatch(setImpersonating(profileUser.impersonating));
    setImpersonationStorage(profileUser.impersonating);
    return;
  }
  if (responseImpersonating === true) {
    dispatch(setImpersonating(true));
    setImpersonationStorage(true);
  }
};

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (state, { payload }) => {
      state.user = payload?.user ?? payload;
      state.isAuthenticated = !!payload;
      state.loading = false;
    },
    setPermissions: (state, { payload }) => {
      state.permissions = payload || [];
    },
    setPermissionsReady: (state, { payload }) => {
      state.permissionsReady = payload !== false;
    },
    setLoading: (state, { payload }) => {
      state.loading = payload ?? true;
    },
    setImpersonating: (state, { payload }) => {
      state.impersonating = !!payload;
    },
    logout: (state) => {
      state.user = null;
      state.permissions = [];
      state.isAuthenticated = false;
      state.loading = false;
      state.permissionsReady = false;
      state.impersonating = false;
    },
    refreshPermissionsSuccess: (state, { payload }) => {
      state.permissions = payload || [];
    },
  },
});

export const {
  setCredentials,
  setPermissions,
  setPermissionsReady,
  setLoading,
  setImpersonating,
  logout: logoutAction,
  refreshPermissionsSuccess,
} = authSlice.actions;

export default authSlice.reducer;

// Thunks - single place for auth API orchestration
export const checkAuth = () => async (dispatch, getState) => {
  const token = localStorage.getItem('token');
  if (!token) {
    dispatch(setLoading(false));
    dispatch(setPermissionsReady(true));
    return;
  }

  try {
    dispatch(setLoading(true));
    dispatch(setPermissionsReady(false));
    const profileRes = await authRequest(dispatch, 'getProfile');
    const data = profileRes?.data ?? profileRes;
    const userData = data?.user ?? data?.data ?? data;

    if (userData) {
      dispatch(setCredentials(userData));
      localStorage.setItem('user', JSON.stringify(userData));
    }

    if (typeof userData?.impersonating === 'boolean') {
      dispatch(setImpersonating(userData.impersonating));
      setImpersonationStorage(userData.impersonating);
    } else if (localStorage.getItem('impersonating') === 'true') {
      dispatch(setImpersonating(true));
    } else {
      dispatch(setImpersonating(false));
      localStorage.removeItem('impersonating');
    }

    const permissions = await loadPermissionsForProfile(dispatch, profileRes, userData);
    dispatch(setPermissions(permissions));
    dispatch(setPermissionsReady(true));

    const userId = userData?.id ?? userData?.uuid;
    if (permissions.length === 0 && userId) {
      toast.warn('User has no permissions assigned');
    }
  } catch (error) {
    console.error('Auth check failed:', error);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    clearImpersonationStorage();
    dispatch(logoutAction());
  } finally {
    dispatch(setLoading(false));
    dispatch(setPermissionsReady(true));
  }
};

export const loginUser = (credentials) => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    clearImpersonationStorage();
    dispatch(setImpersonating(false));
    const response = await authRequest(dispatch, 'login', credentials);
    const data = response?.data ?? response;

    const userData = data?.user ?? data?.data?.user ?? data;
    const token =
      data?.access_token ??
      data?.token ??
      data?.data?.access_token ??
      data?.data?.token;

    if (!token) {
      throw new Error('No token received from server. Please try again.');
    }

    localStorage.setItem('token', token);
    if (userData && typeof userData === 'object') {
      localStorage.setItem('user', JSON.stringify(userData));
    }

    dispatch(setCredentials(userData || { token }));

    const profileRes = await authRequest(dispatch, 'getProfile');
    const profileData = profileRes?.data ?? profileRes;
    const profileUser = profileData?.user ?? profileData?.data ?? profileData;
    if (profileUser) {
      dispatch(setCredentials(profileUser));
      localStorage.setItem('user', JSON.stringify(profileUser));
    }

    const permissions = await loadPermissionsForProfile(dispatch, profileRes, profileUser);
    dispatch(setPermissions(permissions));
    dispatch(setPermissionsReady(true));

    return { data };
  } catch (error) {
    const msg = error.apiMessage || error.response?.data?.message || error?.message || 'Login failed';
    throw new Error(msg);
  } finally {
    dispatch(setLoading(false));
  }
};

export const logoutUser = () => async (dispatch) => {
  try {
    await authRequest(dispatch, 'logout');
  } catch (e) {
    // ignore
  }
  try {
    await logoutOneSignal();
  } catch (e) {
    // ignore
  }
  localStorage.clear();
  dispatch(logoutAction());
  dispatch(baseApi.util.resetApiState());
  toast.success('Logged out');
};

export const refreshPermissions = () => async (dispatch) => {
  try {
    const profileRes = await authRequest(dispatch, 'getProfile');
    const data = profileRes?.data ?? profileRes;
    const userData = data?.user ?? data?.data ?? data;

    const permissions = await loadPermissionsForProfile(dispatch, profileRes, userData);
    dispatch(refreshPermissionsSuccess(permissions));
  } catch (e) {
    console.error('Failed to refresh permissions:', e);
  }
};

export const getProfile = () => async (dispatch) => {
  try {
    const { data } = await authRequest(dispatch, 'getProfile');
    return data?.user ?? data;
  } catch (err) {
    throw new Error(err.response?.data?.message || 'Failed to get profile');
  }
};

/** Fetch profile and update Redux auth state (user + permissions) - returns user data */
export const refreshAuthProfile = () => async (dispatch) => {
  try {
    const profileRes = await authRequest(dispatch, 'getProfile');
    const data = profileRes?.data ?? profileRes;
    const userData = data?.user ?? data?.data ?? data;
    if (userData) {
      dispatch(setCredentials(userData));
      localStorage.setItem('user', JSON.stringify(userData));
    }
    const permissions = await loadPermissionsForProfile(dispatch, profileRes, userData);
    dispatch(setPermissions(permissions));
    return userData;
  } catch (err) {
    throw new Error(err.response?.data?.message || 'Failed to get profile');
  }
};

export const updateProfile = (formData) => async (dispatch) => {
  formData.append('_method', 'PUT');
  try {
    const { data } = await authRequest(dispatch, 'updateProfile', formData);
    return data;
  } catch (err) {
    throw new Error(err.response?.data?.message || 'Profile update failed');
  }
};

export const forgotPassword = (payload) => async (dispatch) => {
  try {
    const { data } = await authRequest(dispatch, 'forgotPassword', payload);
    return data;
  } catch (err) {
    throw new Error(err.response?.data?.message || 'Forgot password failed');
  }
};

export const resetPassword = (payload) => async (dispatch) => {
  try {
    const { data } = await authRequest(dispatch, 'resetPassword', payload);
    return data;
  } catch (err) {
    throw new Error(err.response?.data?.message || 'Reset password failed');
  }
};

export const impersonateUser = (userId) => async (dispatch) => {
  const parentToken = localStorage.getItem('token');
  if (!parentToken) {
    throw new Error('You must be signed in to impersonate a user.');
  }

  const response = await authRequest(dispatch, 'impersonateUser', userId);
  const { token, user: bodyUser, impersonating: responseImpersonating } = extractAuthPayload(response);

  if (!token) {
    throw new Error('No token received from impersonation.');
  }

  localStorage.setItem('impersonation_parent_token', parentToken);
  localStorage.setItem('token', token);
  if (bodyUser && typeof bodyUser === 'object') {
    localStorage.setItem('user', JSON.stringify(bodyUser));
    dispatch(setCredentials(bodyUser));
  }

  const { profileUser } = await hydrateSessionAfterTokenChange(dispatch, token, {
    clearImpersonation: false,
  });

  applyImpersonationFlag(dispatch, profileUser, responseImpersonating);

  const displayName = profileUser?.name || bodyUser?.name || 'user';
  toast.success(`You are now signed in as ${displayName}`);
  return { profileUser };
};

export const leaveImpersonation = () => async (dispatch, getState) => {
  const preLeaveUser = getState().auth.user;
  const preLeaveUserId = getUserId(preLeaveUser);
  const parentToken = localStorage.getItem('impersonation_parent_token');

  try {
    const response = await authRequest(dispatch, 'leaveImpersonation');
    const status = response?.status;
    const { token: bodyToken, user: bodyUser, impersonating: responseImpersonating } =
      extractAuthPayload(response);

    let token = bodyToken;

    if (!token && (status === 200 || status === 201 || status === 204) && parentToken) {
      token = parentToken;
    }

    if (
      token &&
      responseImpersonating === false &&
      preLeaveUserId &&
      bodyUser &&
      isSameUser(bodyUser, preLeaveUser) &&
      parentToken
    ) {
      token = parentToken;
    }

    if (!token) {
      throw new Error('No token received when leaving impersonation.');
    }

    const { profileUser } = await hydrateSessionAfterTokenChange(dispatch, token);

    if (preLeaveUserId && profileUser && isSameUser(profileUser, preLeaveUser) && parentToken) {
      await hydrateSessionAfterTokenChange(dispatch, parentToken);
    }

    toast.success('You are back in your own account');
  } catch (error) {
    if (parentToken) {
      try {
        await hydrateSessionAfterTokenChange(dispatch, parentToken);
        toast.warn(
          error.response?.data?.message ||
            error.message ||
            'Leave impersonation failed; restored your admin session locally.'
        );
        return;
      } catch (restoreErr) {
        console.error('Failed to restore parent session:', restoreErr);
      }
    }
    throw new Error(error.response?.data?.message || error.message || 'Failed to leave impersonation');
  }
};
